import { CommonModule } from '@angular/common';
import {
  AfterContentInit, AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component,
  ElementRef, EventEmitter, HostBinding, HostListener, Input, NgModule, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild
} from '@angular/core';
import { FFacetModule } from '@wipo/w-angular/f-facet';
import { WButtonModule } from '@wipo/w-angular/w-button';
import { animate, style, transition, trigger } from '@angular/animations';
import { getMinMax, getWidestDescendant, inlinePosition, maxZIndex, ResizablePopupContainer, WAdaptiveBase, WUniversalModule } from '@wipo/w-angular/shared';
import { Observable } from 'rxjs';


@Component({
  selector: 'w-dialog',
  templateUrl: './w-dialog.component.html',
  animations: [
    trigger('accordion', [
      transition(':enter', [
        style({ width: '0%', opacity: 1 }),
        animate('200ms', style({ width: '30%', opacity: 1 })),
      ]),
      transition(':leave', [
        style({ width: '30%', opacity: 1 }),
        animate('200ms', style({ width: '0%', opacity: 1 })),
      ]),
    ]),
  ],
  standalone: false
})
/*
@id w-dialog
@name w-dialog
@status release
@description modal container
 */
export class WDialogComponent extends WAdaptiveBase implements OnInit, AfterViewInit, AfterContentInit, AfterViewChecked, OnDestroy, OnChanges {
  @ViewChild('title') title?: ElementRef<HTMLDivElement>;
  @ViewChild('headerContent') headerContent?: ElementRef<HTMLDivElement>;
  @ViewChild('dialog') dialog?: ElementRef<HTMLDivElement>;
  @ViewChild('content') content?: ElementRef<HTMLDivElement>; // content element
  @ViewChild('space') space?: ElementRef<HTMLDivElement>;
  @ViewChild('footer') footer?: ElementRef<HTMLDivElement>;

  @Input() aspectRatio = 1.6; // allow to provide custom aspect ratio
  @Input() shrinkHeightToContent = false; // if width cannot be reduced, shrink height to content, disregard aspect ratio
  @Input() autoResize = true; // fit dialog size to content; set false to size the dialog with CSS (--w-dialog-width variable, default 600px) or style
  @HostBinding('class.b-dialog-fixed-size') get fixedSize() { return !this.autoResize; }

  @Input() closeButton = false; // show close button in the main layout
  @Input() zIndex!: number; // override z-index (on init calculates from largest z-index on page)
  @Input() style: { [key: string]: any } = {}; // style as JSON object { [key: string]: any }
  @Input() styleClass?: string; // @no-doc override default class (use only style instead), left for backward compatibility

  @Input() isComplexDialog: boolean = false; // show "complex" type dialog
  @Input() complexDialogPosition: inlinePosition = 'start'; // position of the complex dialog, inline direction [start,end]
  @Input() footerPosition: inlinePosition = 'end'; // position of the footer content, inline direction [start,end]
  get footerStart(): boolean { return ['start', 'left'].includes(this.footerPosition) };

  @Output() complexDialogClosed: EventEmitter<void> = new EventEmitter<void>(); // @no-doc - we do not need 2 outputs (keeps this for backward compatibility)
  @Output() wDialogClosed: EventEmitter<void> = this.complexDialogClosed; // emits when close button triggered

  hasHeader = this.closeButton;
  hasFooter = false;

  // control resize with content
  private contentResize?: WDialogPopupContainer;
  /** get these from resize manager */
  get showSeparator() { return this.contentResize?.showSeparator ?? false; }
  get dialogDimensions() { return this.contentResize?.dialogDimensions ?? ''; }

  constructor(protected elementRef: ElementRef<HTMLElement>, private cdr: ChangeDetectorRef) {
    super();
  }
  override ngOnInit(): void {
    super.ngOnInit();
    this.zIndex = maxZIndex() + 1;
  }
  ngAfterViewChecked(): void {
    this.calculateFacets(); // recalculate
  }
  private calculateFacets() {
    const hasHeader = (this.headerContent?.nativeElement?.children?.length ?? 0) > 0 || (this.title?.nativeElement?.children?.length ?? 0) > 0 || this.closeButton;
    const hasFooter = (this.footer?.nativeElement?.children?.length ?? 0) > 1; // 1 child is the separator line
    if (hasHeader !== this.hasHeader || hasFooter !== this.hasFooter) {
      // update in the next microtask
      Promise.resolve().then(() => {
        this.hasHeader = hasHeader;
        this.hasFooter = hasFooter
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const closeButton = changes['closeButton'];
    const aspectRatio = changes['aspectRatio'];
    const shrinkHeightToContent = changes['shrinkHeightToContent'];
    const autoResize = changes['autoResize'];
    if (closeButton?.currentValue !== closeButton?.previousValue) {
      this.calculateFacets();
    }
    if (aspectRatio?.currentValue !== aspectRatio?.previousValue || shrinkHeightToContent?.currentValue !== shrinkHeightToContent?.previousValue
      || autoResize?.currentValue !== autoResize?.previousValue) {
      this.updateContentResize(true);
    }
  }
  ngAfterContentInit() {
    this.calculateFacets(); // initial calculation
  }
  private updateContentResize(recalculate = false) {
    this.elementRef.nativeElement.style.setProperty('--w-dialog-aspect-ratio', `${this.aspectRatio}`);
    if (this.contentResize) {
      this.contentResize.aspectRatio = this.aspectRatio;
      this.contentResize.shrinkHeightToContent = this.shrinkHeightToContent;
      this.contentResize.autoResize = this.autoResize;
      if (recalculate) this.contentResize.recalculate();
    }
  }
  ngAfterViewInit(): void {
    if (this.dialog) {
      const contentResize = new WDialogPopupContainer(this.cdr, this.resizeObserver, this.stateResize, this.elementRef.nativeElement, this.dialog.nativeElement);
      /** add specific sections */
      contentResize.headerContentElement = this.headerContent?.nativeElement;
      contentResize.contentElement = this.content?.nativeElement;
      contentResize.spaceElement = this.space?.nativeElement;
      contentResize.footerElement = this.footer?.nativeElement;
      contentResize.shrinkHeightToContent = this.shrinkHeightToContent;
      contentResize.autoResize = this.autoResize;
      this.contentResize = contentResize;
      /** set parameters */
      this.updateContentResize();
      /** start observing resize */
      contentResize.init({
        maxIterations: 11
      });
    }
  }
  complexDialogClose() {
    this.complexDialogClosed.emit();
  }
  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    // Prevent default browser behavior if needed (e.g., stopping form reset)
    event.preventDefault();
    this.complexDialogClose();
  }
}

@NgModule({
  imports: [CommonModule, FFacetModule, WButtonModule, WUniversalModule],
  exports: [WDialogComponent],
  declarations: [WDialogComponent]
})
export class WDialogModule { }


/** implement dialog specific resize and position rules*/
class WDialogPopupContainer extends ResizablePopupContainer {
  public headerContentElement?: HTMLElement;
  public spaceElement?: HTMLElement;
  public contentElement?: HTMLElement;
  public footerElement?: HTMLElement;

  public showSeparator: boolean = false;
  public autoResize = true; // false: do not calculate size, use CSS
  protected sizeCalculated = false; // size variables were set by this container
  protected firstResize = true;
  /** bracket the target width: content can change height in jumps (e.g. table cells wrap),
   * then simple steps oscillate between fitting and scrolling widths until max iterations */
  protected fitWidth = 0; // narrowest width seen where content fits (no scroll, no overflow)
  protected scrollWidth = 0; // widest width seen where content scrolls or overflows
  protected bracketPrecision = 8; // px, stop when the bracket is this narrow
  protected shrinkSlack = 16; // px of free space we accept (dialog slightly larger than needed) instead of more small shrink steps

  constructor(protected cdr: ChangeDetectorRef, protected resizeObserver: ResizeObserver, protected stateResize: Observable<ResizeObserverEntry[]>,
    protected rootElement: HTMLElement, protected resizableElement: HTMLElement) {
    super();
  }
  override reset() {
    super.reset();
    this.firstResize = true;
    this.fitWidth = 0;
    this.scrollWidth = 0;
  }
  /** keep the next width inside the (scrollWidth, fitWidth) bracket, bisect when a step jumps out of it */
  private bracketWidth(currentWidth: number, targetWidth: number, fits: boolean) {
    if (fits) {
      this.fitWidth = this.fitWidth ? Math.min(this.fitWidth, currentWidth) : currentWidth;
    } else {
      this.scrollWidth = Math.max(this.scrollWidth, currentWidth);
    }
    if (!this.fitWidth || !this.scrollWidth || this.scrollWidth >= this.fitWidth) {
      return targetWidth; // not bracketed (yet), or content changed - use the regular step
    }
    if (this.fitWidth - this.scrollWidth <= this.bracketPrecision || !this.canCalculate) {
      // converged (or iterations stopped): settle at the narrowest width that fits
      if (this.canCalculate) this.stopResizeIteration(); // pause and reset (also clears the bracket)
      return this.fitWidth;
    }
    if (targetWidth <= this.scrollWidth || targetWidth >= this.fitWidth) {
      return Math.ceil((this.scrollWidth + this.fitWidth) / 2);
    }
    return targetWidth;
  }
  /** below dialog specific resize implementation */
  public calculateSize() {
    if (!this.autoResize) {
      this.releaseSize();
      return;
    }
    const dialog = this.resizableElement.getBoundingClientRect();
    const dialogStyle = this.resizableElement.getAttribute('style') || '';
    const manualSizing = dialogStyle.indexOf('width') !== -1 || dialogStyle.indexOf('height') !== -1
    if (manualSizing) {
      // release restrictions
      this.rootElement.style.setProperty('--w-dialog-height', 'auto');
      return; // do not auto-resize manually sized dialogs
    }
    const scrollDistance = this.contentElement ? this.contentElement.scrollHeight - this.contentElement.clientHeight : 0;
    const scrollable = scrollDistance > 0;

    this.showSeparator = scrollable;
    /** adjust width */
    /** first check if content scrollable vertically, then try to increase width */
    const { minWidth, maxWidth } = getMinMax(this.resizableElement);
    const currentWidth = Math.ceil(dialog.width);
    const currentHeight = Math.ceil(dialog.height);

    /** if same size - the modification did not work, assuming that the dialog is already at its minimum/maximum width) */
    const sameSize = this.lastWidth === currentWidth;
    this.lastWidth = currentWidth; // update last width, so we can check if it changed next time

    const currentAspectRatio = currentWidth / currentHeight;// current aspect ratio of dialog element

    if (sameSize) {
      /** use current width to keep aspect ratio */
      this.updateDimensionsAndPosition(Math.max(minWidth, Math.min(maxWidth, this.lastWidth)));
    } else if (this.spaceElement && this.contentElement && this.footerElement && this.headerContentElement) {
      /** get boundaries of dialog elements */
      const space = this.spaceElement.getBoundingClientRect();
      const content = this.contentElement.getBoundingClientRect();
      const footer = this.footerElement.getBoundingClientRect();
      const widest = Math.ceil(getWidestDescendant(this.headerContentElement)) + 31; // add 16px left+right padding - 1px (to make sure it is wider than available space, not just fills it ups)
      const minimumWidth = widest > currentWidth ? widest : 0;
      const stepHeight = Math.ceil(space.height);
      /** scrolling */
      /** width where content would just fit (assuming content reflows keeping its area) */
      const estimate = this.estimateWidth(dialog, content, space);
      let scrollBy = 0;
      if (scrollable) {
        /** grow towards the estimate (at least 8px, estimate is not exact e.g. for tables, images) */
        scrollBy = Math.max(8, estimate - currentWidth);
      }

      /** requested height can exceed the viewport, then container clamps the dialog height and shrinking width
       * does not reduce the free space: every step would be the same until max iterations (arbitrary final width).
       * Start from the width matching the real (clamped) height instead */
      const requestedHeight = parseFloat(this.rootElement.style.getPropertyValue('--w-dialog-height'));
      const heightClamped = !scrollable && stepHeight > 0 && requestedHeight - currentHeight > 1;
      const clampedExcess = heightClamped ? Math.max(0, currentWidth - Math.floor(currentHeight * this.aspectRatio)) : 0;

      // recalculate because of aspect ratio only once
      const aspectAdjustmentStep = this.firstResize ? Math.ceil(currentWidth * (1 - this.aspectRatio / currentAspectRatio)) : 0;
      const aspectAdjustmentIncrease = currentAspectRatio < this.aspectRatio ? -aspectAdjustmentStep : 0;
      const aspectAdjustmentDecrease = currentAspectRatio > this.aspectRatio ? aspectAdjustmentStep : 0;
      const overflow = Math.max(footer.bottom - dialog.bottom, 0);
      const increaseBy = overflow + scrollBy + aspectAdjustmentIncrease;
      const fits = !scrollable && overflow === 0;
      /** free space: shrink to the estimate, if it does not help (content does not reflow) use the free space as step */
      const estimatedShrink = fits && stepHeight > 0 && estimate < currentWidth ? currentWidth - estimate : 0;
      const decreaseBy = estimatedShrink > 0 ? estimatedShrink : stepHeight + aspectAdjustmentDecrease + clampedExcess;
      /** content fits and only little free space left: good enough, stop here */
      const smallShrink = fits && increaseBy <= 0 && stepHeight <= this.shrinkSlack;
      const stepWidth = this.adjustSize(currentWidth, increaseBy, smallShrink ? 0 : decreaseBy, overflow === 0, minimumWidth);
      const targetWidth = smallShrink ? stepWidth : this.bracketWidth(currentWidth, stepWidth, fits);
      this.firstResize = false;
      this.updateDimensionsAndPosition(Math.max(minWidth, Math.min(maxWidth, targetWidth)));
    }
  }

  /** estimate the dialog width where all content is visible without free space:
   * content area is kept when width changes (text reflows), header/footer height is fixed,
   * dialog height follows aspect ratio, but it is limited by the viewport height */
  private estimateWidth(dialog: DOMRect, content: DOMRect, space: DOMRect) {
    const el = this.contentElement!;
    const style = getComputedStyle(el);
    const textWidth = content.width - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    const area = textWidth * el.scrollHeight;
    const sideSpace = dialog.width - textWidth; // width not available for content
    const fixedHeight = dialog.height - content.height - space.height; // header, footer, separators
    const r = this.aspectRatio;
    // width / r = fixedHeight + area / (width - sideSpace)  =>  width^2 - b * width + c = 0
    const b = sideSpace + r * fixedHeight;
    const c = r * fixedHeight * sideSpace - r * area;
    let width = (b + Math.sqrt(b * b - 4 * c)) / 2;
    // viewport limits the height (no margin): content has to fit in the viewport height instead
    const maxHeight = window.innerHeight;
    if (width / r > maxHeight && maxHeight > fixedHeight) {
      width = sideSpace + area / (maxHeight - fixedHeight);
    }
    return Math.ceil(width);
  }

  /** autoResize off: remove calculated size variables, so CSS (--w-dialog-width etc.) or style decide the size */
  private releaseSize() {
    if (this.sizeCalculated) {
      ['--w-dialog-width', '--w-dialog-height', '--w-dialog-margin'].forEach(p => this.rootElement.style.removeProperty(p));
      this.sizeCalculated = false;
    }
    const scrollDistance = this.contentElement ? this.contentElement.scrollHeight - this.contentElement.clientHeight : 0;
    this.showSeparator = scrollDistance > 0;
  }

  private updateDimensionsAndPosition(targetWidth: number) {
    this.sizeCalculated = true;
    const targetHeight = this.updateGetHeight(targetWidth);

    /** top/bottom margin forces the height */
    let margin = Math.floor((window.innerHeight - targetHeight) / 2);
    this.rootElement.style.setProperty('--w-dialog-height', `${targetHeight}px`);
    const space = this.spaceElement?.getBoundingClientRect();
    if (this.shrinkHeightToContent && space && space.height > 0) {
      // adjust/shrink
      const targetHeightAdjusted = Math.ceil(targetHeight - space.height);
      this.rootElement.style.setProperty('--w-dialog-height', `${targetHeightAdjusted}px`);
      margin = Math.floor((window.innerHeight - targetHeightAdjusted) / 2);
    }

    /** if margin becomes negative (window height is smaller that dialog target height), dialog shrinks to window height */
    this.rootElement.style.setProperty('--w-dialog-margin', margin > 0 ? `${margin}px 0 0` : '0');
  }
}